export interface SignupFirstTouch {
    capturedAt: string;
    landingPath: string;
    referrer: string | null;
    utmSource: string | null;
    utmMedium: string | null;
    utmCampaign: string | null;
    utmTerm: string | null;
    utmContent: string | null;
    gclid: string | null;
    fbclid: string | null;
}

export namespace SignupFirstTouch {
    export const URL_PARAM = '_st';

    export function parse(params: { url: string; referrer: string | null; capturedAt: Date }): SignupFirstTouch {
        const parsed = new URL(params.url);
        const query = parsed.searchParams;

        return {
            capturedAt: params.capturedAt.toISOString(),
            landingPath: parsed.pathname,
            referrer: normalizeReferrer({ referrer: params.referrer, currentOrigin: parsed.origin }),
            utmSource: nonEmpty(query.get('utm_source')),
            utmMedium: nonEmpty(query.get('utm_medium')),
            utmCampaign: nonEmpty(query.get('utm_campaign')),
            utmTerm: nonEmpty(query.get('utm_term')),
            utmContent: nonEmpty(query.get('utm_content')),
            gclid: nonEmpty(query.get('gclid')),
            fbclid: nonEmpty(query.get('fbclid')),
        };
    }

    export function pickEarliest(candidates: (SignupFirstTouch | null | undefined)[]): SignupFirstTouch | null {
        let best: SignupFirstTouch | null = null;
        for (const candidate of candidates) {
            if (!candidate) continue;
            if (!best || candidate.capturedAt < best.capturedAt) best = candidate;
        }
        return best;
    }

    // Returns a plain JSON string. Callers should pass it through
    // URLSearchParams (or equivalent) which handles URL encoding once —
    // an extra encodeURIComponent here would just inflate the param ~30%.
    export function encodeForUrl(value: SignupFirstTouch): string {
        return JSON.stringify(value);
    }

    export function decodeFromUrl(encoded: string): SignupFirstTouch | null {
        try {
            const parsed = JSON.parse(encoded);
            if (!isSignupFirstTouchShape(parsed)) return null;
            return parsed;
        } catch {
            return null;
        }
    }
}

function normalizeReferrer(params: { referrer: string | null; currentOrigin: string }): string | null {
    const referrer = nonEmpty(params.referrer);
    if (!referrer) return null;
    try {
        const parsed = new URL(referrer);
        if (parsed.origin === params.currentOrigin) return null;
        return referrer;
    } catch {
        return null;
    }
}

function nonEmpty(value: string | null): string | null {
    if (value === null) return null;
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    return trimmed;
}

function isSignupFirstTouchShape(value: unknown): value is SignupFirstTouch {
    if (typeof value !== 'object' || value === null) return false;
    const v = value as Record<string, unknown>;
    if (typeof v.capturedAt !== 'string' || typeof v.landingPath !== 'string') return false;
    const nullableStringFields = [
        'referrer',
        'utmSource',
        'utmMedium',
        'utmCampaign',
        'utmTerm',
        'utmContent',
        'gclid',
        'fbclid',
    ];
    return nullableStringFields.every((field) => v[field] === null || typeof v[field] === 'string');
}
